import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { EmptyState } from '@/components/PageHeader';
import { fmtDateLong, fmtDateShort } from '@/lib/dates';
import { formatQuantity } from '@/lib/format';

export interface DailyChartProps {
  series: Array<{ date: string; actual: string }>;
  planPerDay: string | null;
  unit: string;
  highlight?: boolean | undefined;
}

/**
 * W3 chart: one y-scale, the actual series in orange with hover values and the
 * plan/day as a dashed reference line (mockup "chart (single scale, two marks, hover)").
 */
export function DailyChart({ series, planPerDay, unit, highlight }: DailyChartProps) {
  if (series.length === 0)
    return <EmptyState>No production logged for this code in the last 30 days.</EmptyState>;
  const data = series.map((p) => ({ date: p.date, actual: Number(p.actual) }));
  const plan = planPerDay !== null ? Number(planPerDay) : null;
  const max = Math.max(...data.map((d) => d.actual), plan ?? 0) * 1.15;

  return (
    <div>
      <div className="mb-2 flex flex-wrap gap-4 text-xs text-ink2">
        <span className="inline-flex items-center gap-1.5">
          <i className="inline-block w-[18px] border-t-2 border-orange" /> Actual {unit}
        </span>
        {plan !== null ? (
          <span className="inline-flex items-center gap-1.5">
            <i className="inline-block w-[18px] border-t-2 border-dashed border-ink3" /> Plan{' '}
            {formatQuantity(plan)} / day
          </span>
        ) : null}
      </div>
      <div
        className="h-56 w-full"
        role="img"
        aria-label={`Daily actual ${unit} for the last ${data.length} logged days${plan !== null ? ` against a plan of ${formatQuantity(plan)} per day` : ''}`}
      >
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: -12 }}>
            <CartesianGrid vertical={false} stroke="var(--line2)" />
            <XAxis
              dataKey="date"
              tickFormatter={fmtDateShort}
              tick={{ fill: 'var(--ink3)', fontSize: 11 }}
              axisLine={{ stroke: 'var(--line2)' }}
              tickLine={false}
              minTickGap={24}
            />
            <YAxis
              domain={[0, Math.ceil(max)]}
              tick={{ fill: 'var(--ink3)', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={44}
              tickFormatter={(v: number) => formatQuantity(v)}
            />
            <Tooltip
              cursor={{ stroke: 'var(--line)', strokeDasharray: '3 3' }}
              content={({ active, payload }) => {
                const p = payload?.[0]?.payload as { date: string; actual: number } | undefined;
                if (!active || !p) return null;
                return (
                  <div className="rounded-md bg-ink px-2.5 py-1.5 text-xs text-on-ink shadow num">
                    <div className="opacity-70">{fmtDateLong(p.date)}</div>
                    <div className="font-semibold">
                      {formatQuantity(p.actual)} {unit}
                      {plan !== null && plan > 0 ? (
                        <span className="ml-1.5 opacity-70">
                          {Math.round((p.actual / plan) * 100)}% of plan
                        </span>
                      ) : null}
                    </div>
                  </div>
                );
              }}
            />
            {plan !== null ? (
              <ReferenceLine
                y={plan}
                stroke="var(--ink3)"
                strokeDasharray="5 5"
                strokeWidth={2}
                label={{
                  value: `plan ${formatQuantity(plan)}`,
                  position: 'insideTopRight',
                  fill: 'var(--ink2)',
                  fontSize: 11,
                  fontWeight: 600,
                }}
              />
            ) : null}
            <Line
              type="monotone"
              dataKey="actual"
              stroke="var(--orange)"
              strokeWidth={highlight ? 3 : 2}
              dot={{ r: 3.5, fill: 'var(--orange)', stroke: 'var(--surface)', strokeWidth: 2 }}
              activeDot={{ r: 5 }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
